import { html, render, type TemplateResult } from 'lit-html';
import styles from './pk-totp-setup-card.css?inline';
import '../pk-card/pk-card.js';
import '../pk-qr-code/pk-qr-code.js';

/**
 * `<pk-totp-setup-card>` — TOTP enrolment surface.
 *
 * Wraps a `<pk-card>` and displays:
 *   - Eyebrow "Two-factor auth"
 *   - H1 "Secure your account"
 *   - Lede copy
 *   - QR code via `<pk-qr-code>` (when qr-png is set), with a loading
 *     placeholder when not yet available
 *   - Collapsible `<details>/<summary>` for the manual secret key
 *   - 6-digit verification code `<pk-form-field>` (code variant)
 *   - "Verify & activate" primary submit button with loading state
 *   - Optional "Skip for now" foot link (hidden when skip-url is absent)
 *
 * Attributes:
 *   - `action`     — form POST URL (default: /portal/totp/setup)
 *   - `skip-url`   — URL for the "Skip for now" link; link hidden when absent
 *   - `qr-png`     — data URI for the QR code image (server-generated)
 *   - `manual-key` — plain-text TOTP secret for manual entry
 *   - `label`      — account label shown in the manual-key section
 *   - `error`      — error message string; renders a pk-alert when set
 *   - `csrf-token` — anti-forgery token value; renders a hidden input in the form
 *   - `csrf-name`  — CSRF hidden-input name (default `__RequestVerificationToken`)
 *   - `eyebrow`    — small uppercase brand label. Default: `Two-factor auth`.
 *   - `title`      — main heading. Default: `Secure your account`.
 *   - `lede`       — paragraph beneath the heading. Override for non-ActivityLog products.
 *
 * Slots:
 *   - `foot` — extra links / affordances rendered below the form (and below the
 *     skip-url link when one is rendered). Use for escape-hatch actions like
 *     "Sign out" when an admin past their TOTP grace can't enrol on the current
 *     device. Mirrors the `foot` slot pattern on `<pk-login-card>` and
 *     `<pk-register-card>`.
 */
export class PkTotpSetupCard extends HTMLElement {
    static readonly tagName = 'pk-totp-setup-card';

    static get observedAttributes(): string[] {
        return [
            'action', 'skip-url', 'qr-png', 'manual-key', 'label', 'error',
            'csrf-token', 'csrf-name',
            'eyebrow', 'title', 'lede',
        ];
    }

    private readonly _root: ShadowRoot;
    private _submitting = false;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this.render();
        this._root.addEventListener('submit', this._handleSubmit);
    }

    disconnectedCallback(): void {
        this._root.removeEventListener('submit', this._handleSubmit);
    }

    attributeChangedCallback(): void {
        this.render();
    }

    private readonly _handleSubmit = (e: Event): void => {
        const form = e.target as HTMLFormElement;
        if (!form.classList.contains('totp-form')) return;
        this._submitting = true;
        this.render();
    };

    private get _action(): string {
        return this.getAttribute('action') ?? '/portal/totp/setup';
    }

    private get _skipUrl(): string {
        return this.getAttribute('skip-url') ?? '';
    }

    private get _qrPng(): string {
        return this.getAttribute('qr-png') ?? '';
    }

    private get _manualKey(): string {
        return this.getAttribute('manual-key') ?? '';
    }

    private get _label(): string {
        return this.getAttribute('label') ?? '';
    }

    private get _error(): string {
        return this.getAttribute('error') ?? '';
    }

    private get _csrfToken(): string {
        return this.getAttribute('csrf-token') ?? '';
    }

    private get _csrfName(): string {
        return this.getAttribute('csrf-name') ?? '__RequestVerificationToken';
    }

    private get _eyebrow(): string {
        return this.getAttribute('eyebrow') ?? 'Two-factor auth';
    }

    private get _title(): string {
        return this.getAttribute('title') ?? 'Secure your account';
    }

    private get _lede(): string {
        return this.getAttribute('lede')
            ?? 'Scan the QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc.), then enter the 6-digit code to confirm.';
    }

    private _manualKeyTpl(manualKey: string, label: string): TemplateResult {
        const labelTpl = label ? html`<p class="manual-key-label">${label}</p>` : '';
        return html`
            <details class="manual-key-details">
                <summary class="manual-key-summary">
                    Can't scan? Enter the key manually
                </summary>
                <div class="manual-key-body">
                    ${labelTpl}
                    <code class="manual-key-code">${manualKey}</code>
                    <p class="manual-key-hint">
                        Enter this key in your authenticator app to set up your account.
                        Time-based one-time password (TOTP).
                    </p>
                </div>
            </details>
        `;
    }

    private template(): TemplateResult {
        const action     = this._action;
        const skipUrl    = this._skipUrl;
        const qrPng      = this._qrPng;
        const manualKey  = this._manualKey;
        const label      = this._label;
        const error      = this._error;
        const csrfToken  = this._csrfToken;
        const csrfName   = this._csrfName;
        const eyebrow    = this._eyebrow;
        const heading    = this._title;
        const lede       = this._lede;
        const submitting = this._submitting;

        return html`
            <style>${styles}</style>
            <pk-card>
                <span slot="eyebrow" class="eyebrow">${eyebrow}</span>
                <h1>${heading}</h1>
                <p class="lede">${lede}</p>

                ${error ? html`
                    <pk-alert variant="error" class="form-error">${error}</pk-alert>
                ` : ''}

                <div class="qr-section">
                    ${qrPng
                        ? html`<pk-qr-code
                                src=${qrPng}
                                label="Scan this QR code in your authenticator app"
                                size="200"
                            ></pk-qr-code>`
                        : html`<div class="qr-loading" aria-label="QR code loading" role="img">
                                <span class="qr-loading-text" aria-hidden="true">Loading QR code…</span>
                            </div>`
                    }
                </div>

                ${manualKey ? this._manualKeyTpl(manualKey, label) : ''}

                <form
                    class="totp-form"
                    method="post"
                    action=${action}
                    novalidate
                >
                    ${csrfToken ? html`
                        <input type="hidden" name=${csrfName} .value=${csrfToken} />
                    ` : ''}
                    <pk-form-field
                        name="code"
                        type="text"
                        label="Verification code"
                        autocomplete="one-time-code"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        maxlength="6"
                        code
                        required
                    ></pk-form-field>

                    <pk-button
                        variant="primary"
                        type="submit"
                        class="submit-btn"
                        ?data-submitting=${submitting}
                        loading-label="Verifying…"
                        ?disabled=${submitting}
                        aria-busy=${submitting ? 'true' : 'false'}
                    >Verify &amp; activate</pk-button>
                </form>

                <div slot="foot">
                    ${skipUrl ? html`
                        <a href=${skipUrl} class="skip-link">Skip for now</a>
                    ` : ''}
                    <slot name="foot"></slot>
                </div>
            </pk-card>
        `;
    }

    private render(): void {
        render(this.template(), this._root);
    }
}

if (!customElements.get(PkTotpSetupCard.tagName)) {
    customElements.define(PkTotpSetupCard.tagName, PkTotpSetupCard);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-totp-setup-card': PkTotpSetupCard;
    }
}
